import { MODAL_UPGRADE_PLAN } from '@components/App/Modal/constants'
import { usePasteFromToolbar } from '@components/ArtBoard/ArtboardToolbar'
import type {
  BlockWireframe,
  ID,
  SymbolBlock,
} from '@octopus-do/octopus-project-core'
import { BlockActions } from '@store/blockSaga'
import {
  thunkChangeBlockCompleted,
  thunkChangeBlockWireframes,
  thunkConvertBlockToSymbol,
  thunkCopySelectedBlock,
  thunkUnlinkSymbol,
  thunkUpdateBlockColor,
} from '@store/migrationActions/thunkActions/thunkBlocksActions'
import { thunkUpdateSymbolVisual } from '@store/migrationActions/thunkActions/thunkSymbolEdit'
import { migrationProjectSelector } from '@store/migrationSelectors/projectSelectors'
import { artBoardActions } from '@store/reducers/artboard'
import type { AppState } from '@store/types'
import { uploadImage } from '@storeOld/reducers/blocks'
import {
  CanvasActionTypes,
  setUploadingImage,
} from '@storeOld/reducers/canvas/canvas'
import {
  NotificationType,
  notificationActionCreators,
} from '@storeOld/reducers/notification'
import { openModal } from '@storeOld/reducers/ui'
import type React from 'react'
import { useRef } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import type { Action } from 'redux'
import type { ThunkDispatch } from 'redux-thunk'

import { GtmTriggerName, sendGtmEvent } from '@/constants/gtm'
import { useArtboardMode, useProjectAccess } from '@/hooks'
import { BLOCK_PERMISSION } from '@/permissions/project/definitions'
import { clearBlockImage } from '@/store_old/reducers/blocks'

export const useBlockToolbar = (blockId: ID) => {
  const dispatch = useDispatch<ThunkDispatch<AppState, unknown, Action>>()

  const block = useSelector(
    migrationProjectSelector.getProjectBlockOrSymbolData(blockId),
    shallowEqual
  )

  const { symbolLibrary } = useArtboardMode()

  const multiple = useSelector<AppState, boolean>(
    ({ canvas }) => canvas.block.ids.length > 1
  )

  const autoCreated = useSelector<AppState, boolean>(
    ({ canvas }) => canvas.block.autoCreatedBlockId !== null
  )

  const imageUploading = useSelector<AppState, boolean>(
    ({ canvas }) =>
      canvas.uploadingImage.id === blockId && canvas.uploadingImage.uploading
  )

  const resolveAccess = useProjectAccess(BLOCK_PERMISSION.RESOLVE)
  const imageAccess = useProjectAccess(BLOCK_PERMISSION.IMAGE)
  const arrowsAccess = useProjectAccess(BLOCK_PERMISSION.ARROWS)
  const symbolAccess = useProjectAccess(BLOCK_PERMISSION.MAKE_SYMBOL)
  const duplicateAccess = useProjectAccess(BLOCK_PERMISSION.DUPLICATE)

  const imageInputRef = useRef<HTMLInputElement>(null)
  const { onPaste, pasting } = usePasteFromToolbar('block')

  const { colorId, completed, type, visual } = block
  const monolithic = type === 'monolithic'
  const symbol = type === 'symbol'
  const imageAttached = visual.type === 'image'
  const wireframes = visual.type === 'wireframes' ? visual.wireframes : []

  const imageLocked =
    imageAccess.hasNotAvailableAccess || imageAccess.hasNotAvailableAccessBadge

  const clearSelection = () => {
    dispatch({ type: CanvasActionTypes.CLEAR_SELECTED_BLOCKS })
  }

  const onComplete = () => {
    if (!resolveAccess.hasAccess) {
      return
    }

    sendGtmEvent(
      GtmTriggerName.BLOCK_COMPLETE,
      'Click to complete block from block action toolbar'
    )

    dispatch(thunkChangeBlockCompleted({ id: blockId, completed: !completed }))
    clearSelection()
  }

  const onAddBlock = () => {
    sendGtmEvent(GtmTriggerName.BLOCK_ADD)
    dispatch(BlockActions.addBlock())
  }

  const onChangeColor = (colorId: ID) => {
    sendGtmEvent(
      GtmTriggerName.BLOCK_COLOR,
      'Change block color in color dropdown from block action toolbar'
    )
    dispatch(thunkUpdateBlockColor(colorId))
  }

  const onWireframesChange = (wireframes: BlockWireframe[]) => {
    dispatch(thunkChangeBlockWireframes(wireframes))
  }

  const removeImage = () => {
    if (monolithic) {
      dispatch(clearBlockImage())
    }

    const { symbolId } = block as unknown as SymbolBlock

    dispatch(
      thunkUpdateSymbolVisual({
        id: symbolId,
        visual: { wireframes: [], type: 'wireframes' },
      })
    )
  }

  const onImage = () => {
    if (imageLocked) {
      sendGtmEvent(GtmTriggerName.UPGRADE_BLOCK_IMAGE)
      dispatch(openModal(MODAL_UPGRADE_PLAN))
      return
    }

    if (imageAttached) {
      removeImage()
      return
    }

    sendGtmEvent(
      GtmTriggerName.BLOCK_IMAGE,
      'Click to add image to block from block action toolbar'
    )
    imageInputRef.current?.click()
  }

  // The toolbar button and the block show a loader while the file is in flight
  const onImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.item(0)

    if (!file || e.target.files?.length !== 1) {
      return
    }

    dispatch(setUploadingImage(true))

    try {
      await dispatch(uploadImage(file))
    } catch (error) {
      console.error('upload image:', error)
    } finally {
      dispatch(setUploadingImage(false))
    }
  }

  const onArrows = () => {
    sendGtmEvent(GtmTriggerName.ARROW_ADD, 'Click on arrow button in toolbar')
    dispatch(artBoardActions.startArrows({ id: blockId, kind: 'block' }))
  }

  const onSymbol = () => {
    if (symbolAccess.hasNotAvailableAccess) {
      sendGtmEvent(GtmTriggerName.UPGRADE_SYMBOLS)
      dispatch(
        openModal({
          modalType: MODAL_UPGRADE_PLAN,
          modalProps: { slideType: 'symbol' },
        })
      )
      return
    }

    if (symbol) {
      dispatch(thunkUnlinkSymbol(blockId))
      return
    }

    sendGtmEvent(
      GtmTriggerName.SYMBOL_ADD_TOOLBAR,
      'Click to make symbol from block action toolbar'
    )
    dispatch(thunkConvertBlockToSymbol(blockId))
  }

  const onCopy = () => {
    dispatch(thunkCopySelectedBlock())
    dispatch(notificationActionCreators.setClearAllNotification())
    dispatch(
      notificationActionCreators.setNotification({
        type: NotificationType.COPY_SUCCESSFUL,
      })
    )
  }

  const onDuplicate = () => dispatch(BlockActions.duplicateBlock())

  const onDelete = () => dispatch(BlockActions.deleteBlock())

  return {
    actions: {
      onAddBlock,
      onArrows,
      onChangeColor,
      onComplete,
      onCopy,
      onDelete,
      onDuplicate,
      onImage,
      onImageChange,
      onOpenColorLibrary: clearSelection,
      onPaste,
      onSymbol,
      onWireframesChange,
    },
    colorId,
    completed,
    completeDisabled: multiple || resolveAccess.hasNotAvailableAccess,
    completeHidden:
      !resolveAccess.hasAccess && !resolveAccess.hasNotAvailableAccess,
    addBlockDisabled: multiple || symbolLibrary,
    imageAttached,
    imageInputRef,
    imageLocked,
    imageShown: monolithic || imageAttached,
    imageUploading,
    wireframes,
    arrowsDisabled:
      multiple || arrowsAccess.hasNotAvailableAccess || autoCreated,
    symbol,
    symbolDisabled: multiple,
    symbolLocked: symbolAccess.hasNotAvailableAccess,
    duplicateDisabled:
      multiple || duplicateAccess.hasNotAvailableAccess || autoCreated,
    pasteDisabled: multiple || symbolLibrary,
    pasting,
  }
}
